import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useMeetings,
  useAnalyzeMeeting,
  useMeetingToDecision,
  type Meeting,
  type MeetingAnalysis,
} from "../lib/meetings";
import "../styles.w3.css";

/**
 * Meeting-Cockpit v1 (CompanyBrain Welle 3, BEN-107) - read-first, KI-vermittelt, grounded.
 * Zeigt AUSSCHLIESSLICH real indexierte Meeting-Inhalte (Meetily/Nextcloud-Protokolle aus
 * dem KIM-Lakehouse). Je Meeting: KI-Zusammenfassung + Action-Items + Entscheidungen
 * (lokal generiert, grounded auf die echten Transkript-Chunks, mit Quellenbeleg).
 * Action-Items koennen als Vorschlag in den Decision-Log uebernommen werden.
 * OpenProject-Uebertrag bleibt DRY-RUN/gated.
 */

function KindBadge({ kind }: { kind: string }) {
  return <span className="w3-pill kind">{kind}</span>;
}

function AnalysisPanel({ meeting }: { meeting: Meeting }) {
  const analyze = useAnalyzeMeeting();
  const toDecision = useMeetingToDecision();
  const [result, setResult] = useState<MeetingAnalysis | null>(null);
  const [taken, setTaken] = useState<Record<string, string>>({});

  function run() {
    analyze.mutate(meeting.file, { onSuccess: (r) => setResult(r) });
  }
  function take(text: string, key: string) {
    toDecision.mutate(
      { file: meeting.file, text, title: text.slice(0, 100) },
      { onSuccess: (res) => setTaken((t) => ({ ...t, [key]: res.decision.id })) },
    );
  }

  return (
    <div className="w3-analysis">
      <div className="w3-analysis-head">
        <div>
          <h3>{meeting.title}</h3>
          <p className="muted small">
            <KindBadge kind={meeting.kind} /> · Quelle {meeting.source || "–"} · {meeting.chunk_count} Chunk(s)
            {meeting.date ? ` · ${meeting.date}` : ""}
          </p>
        </div>
        <button className="btn primary" onClick={run} disabled={analyze.isPending}>
          {analyze.isPending ? "KI analysiert …" : result ? "Neu analysieren" : "✳ KI-Zusammenfassung erstellen"}
        </button>
      </div>

      {!result && !analyze.isPending && (
        <p className="muted small">
          Die lokale KI (DeepSeek) fasst dieses Meeting zusammen und extrahiert Action-Items und Entscheidungen
          &mdash; ausschliesslich aus dem echten Protokolltext, mit Quellenbeleg. Nichts wird erfunden.
        </p>
      )}

      {result && (
        <div className="w3-analysis-body">
          <div className="w3-summary">
            <h4>Zusammenfassung</h4>
            <p>{result.summary || "—"}</p>
          </div>

          <div className="w3-cols">
            <div className="w3-col">
              <h4>Action-Items <em className="muted small">({result.action_items.length})</em></h4>
              {result.action_items.length === 0 && <p className="muted small">Keine Action-Items im Protokoll erkannt.</p>}
              <ul className="w3-items">
                {result.action_items.map((a, i) => (
                  <li key={i}>
                    <span>{a.text}{a.owner ? <em className="w3-owner"> · {a.owner}</em> : null}</span>
                    {taken[`a${i}`] ? (
                      <Link className="w3-took" to="/app/decisions">→ im Log</Link>
                    ) : (
                      <button className="btn ghost xsmall" onClick={() => take(a.text, `a${i}`)} disabled={toDecision.isPending}
                        title="Als Vorschlag in den Entscheidungs-Log uebernehmen (Status offen, mit Quellenbeleg).">
                        Als Entscheidung
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="w3-col">
              <h4>Entscheidungen <em className="muted small">({result.decisions.length})</em></h4>
              {result.decisions.length === 0 && <p className="muted small">Keine Entscheidungen im Protokoll erkannt.</p>}
              <ul className="w3-items">
                {result.decisions.map((x, i) => (
                  <li key={i}>
                    <span>{x.text}</span>
                    {taken[`d${i}`] ? (
                      <Link className="w3-took" to="/app/decisions">→ im Log</Link>
                    ) : (
                      <button className="btn ghost xsmall" onClick={() => take(x.text, `d${i}`)} disabled={toDecision.isPending}>
                        In Log
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="w3-source muted small">
            <strong>Quelle (grounded):</strong> {meeting.file}
            {result.document_ids?.[0] ? ` · doc ${result.document_ids[0].slice(0, 12)}…` : ""} ·
            {` ${result.chunks_used} Chunk(s) verwendet`} · Modell {result.engine}
            {" · "}<span title="OpenProject-Uebertrag ist bewusst deaktiviert (Dry-Run) bis zur Freigabe.">
              OpenProject-Uebertrag: {result.openproject_write_enabled ? "aktiv" : "Dry-Run (gated)"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MeetingsPage() {
  const q = useMeetings();
  const [selected, setSelected] = useState<string | null>(null);
  const meetings = q.data?.meetings ?? [];
  const selectedMeeting = meetings.find((m) => m.id === selected) ?? null;

  return (
    <section className="w3-page" aria-label="Meeting-Cockpit">
      <header className="w3-hero">
        <div>
          <p className="hero-eyebrow">CompanyBrain · Welle 3</p>
          <h1>Meeting-Cockpit</h1>
          <p className="muted">
            Ihre <strong>real indexierten Meeting-Protokolle</strong> (Meetily &amp; Nextcloud) an einem Ort. Die
            lokale KI erstellt auf Wunsch je Meeting eine <strong>Zusammenfassung, Action-Items und Entscheidungen</strong>
            &mdash; grounded auf die echten Transkripte, mit Quellenbeleg. Nichts wird erfunden.
          </p>
        </div>
      </header>

      {q.isLoading && <p className="muted">Meetings werden geladen …</p>}

      {!q.isLoading && q.data?.degraded && (
        <p className="w3-warn small">Der Wissensindex ist derzeit nicht erreichbar &mdash; bitte spaeter erneut versuchen.</p>
      )}

      {!q.isLoading && !q.data?.degraded && meetings.length === 0 && (
        <div className="w3-empty">
          <p><strong>Noch keine Meeting-Protokolle indexiert.</strong></p>
          <p className="muted small">
            Sobald Protokolle (Meetily/Nextcloud) in die Wissensbasis aufgenommen sind, erscheinen sie hier
            automatisch &mdash; mit KI-Zusammenfassung und Action-Items.
          </p>
        </div>
      )}

      {meetings.length > 0 && (
        <div className="w3-mtg-layout">
          <div className="w3-mtg-list">
            <p className="muted small">{meetings.length} indexierte Meetings · Klicken zum Analysieren</p>
            {meetings.map((m) => (
              <article key={m.id} className={`w3-card mtg-card ${selected === m.id ? "active" : ""}`}
                onClick={() => setSelected(m.id)} tabIndex={0} role="button"
                onKeyDown={(e) => { if (e.key === "Enter") setSelected(m.id); }}>
                <div className="w3-card-top">
                  <h3>{m.title}</h3>
                  <KindBadge kind={m.kind} />
                </div>
                <div className="w3-card-meta muted small">
                  {m.date && <span>{m.date}</span>}
                  <span>· {m.chunk_count} Chunk(s)</span>
                  {m.source && <span>· {m.source.split(":")[0]}</span>}
                </div>
              </article>
            ))}
          </div>
          <div className="w3-mtg-detail">
            {selectedMeeting
              ? <AnalysisPanel key={selectedMeeting.id} meeting={selectedMeeting} />
              : <div className="w3-empty small"><p className="muted">Waehlen Sie links ein Meeting, um die KI-Zusammenfassung und Action-Items zu erzeugen.</p></div>}
          </div>
        </div>
      )}
    </section>
  );
}
